import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { addClassNamesToElement } from "@lexical/utils";
import { headlessDecorate } from "./headless-decorate";
import { convertDividerElement, isDividerElement } from "./utils";

import type { InsertDividerCommand } from "../types/commands";
import type { DividerStyleOverride } from "../types/divider-style";
import type { SerializedDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type {
  DOMConversionMap,
  DOMExportOutput,
  ElementFormatType,
  LexicalNode,
  Spread,
} from "lexical";

export type SerializedDividerNode = Spread<
  {
    type: "divider";
    divider: InsertDividerCommand;
    styleOverride?: DividerStyleOverride;
  },
  SerializedDecoratorBlockNode
>;

export class DividerNode extends DecoratorBlockNode {
  __divider: InsertDividerCommand;
  __styleOverride: DividerStyleOverride;

  constructor(
    divider: InsertDividerCommand,
    styleOverride: DividerStyleOverride = {},
    format?: ElementFormatType,
    key?: string,
  ) {
    super(format, key);
    this.__divider = divider;
    this.__styleOverride = styleOverride;
  }

  getStyleOverride(): DividerStyleOverride {
    return this.getLatest().__styleOverride;
  }

  setStyleOverride(styleOverride: DividerStyleOverride): void {
    this.getWritable().__styleOverride = styleOverride;
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  static getType(): string {
    return "divider";
  }

  static clone(node: DividerNode): DividerNode {
    return new this(node.__divider, node.__styleOverride, node.__format, node.__key);
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("span");
    addClassNamesToElement(dom, "decorator-block-node");
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const divider = document.createElement("hr");
    divider.setAttribute("data-divider-type", this.__divider?.type ?? "");
    return { element: divider };
  }

  getTextContent(): string {
    return "\n";
  }

  static importJSON(node: SerializedDividerNode): DividerNode {
    return new this(node.divider, node.styleOverride, node.format);
  }

  exportJSON(): SerializedDividerNode {
    const serialized: SerializedDividerNode = {
      ...super.exportJSON(),
      type: "divider",
      divider: this.__divider,
      format: this.__format,
    };
    // Dividers that follow the email design save as they did before overrides existed.
    if (Object.keys(this.__styleOverride).length === 0) {
      return serialized;
    }
    return { ...serialized, styleOverride: this.__styleOverride };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      hr: (node: HTMLElement) => {
        if (!isDividerElement(node)) return null;

        return {
          conversion: (n) => convertDividerElement(n, DividerNode),
          priority: 0,
        };
      },
    };
  }
}

export function $isDividerNode(node: LexicalNode | null | undefined): node is DividerNode {
  return node instanceof DividerNode;
}
